import { useEffect, useState, type ReactElement } from 'react';
import { Icon } from './Icon';

/**
 * Der Hinweis, dass der Schnittplatz auf den Home-Bildschirm gehört.
 *
 * Auf Android fragt der Browser selbst. Auf dem iPhone tut er das nicht und
 * hat es nie getan — dort geht es nur über *Teilen › Zum Home-Bildschirm*, und
 * wer das nicht weiß, arbeitet für immer in einem Browserfenster mit einer
 * Adressleiste, die ein Fünftel des Bildes frisst.
 *
 * Deshalb dieser eine Streifen. Er kommt **einmal**: wer ihn wegtippt, sieht
 * ihn nicht wieder, und wer die App schon vom Home-Bildschirm aus gestartet
 * hat, sieht ihn nie. Ein Hinweis, der wiederkommt, ist Werbung.
 */
const GESEHEN = 'schnitt.homebildschirm';

function istIPhone(): boolean {
  const ua = navigator.userAgent;
  // iPadOS gibt sich seit Jahren als Mac aus; der Berührungsbildschirm verrät es.
  const iOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
  // Nur Safari: in Chrome auf iOS gibt es „Zum Home-Bildschirm“ gar nicht.
  return iOS && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

function schonInstalliert(): boolean {
  const alsApp = window.matchMedia?.('(display-mode: standalone)').matches ?? false;
  return alsApp || (navigator as { standalone?: boolean }).standalone === true;
}

export function AufDenSchirm(): ReactElement | null {
  const [zeigen, setZeigen] = useState(false);

  useEffect(() => {
    if (!istIPhone() || schonInstalliert()) return;
    try {
      if (localStorage.getItem(GESEHEN) === 'ja') return;
    } catch {
      // Ein Browser ohne Speicher zeigt ihn eben jedes Mal. Immer noch besser
      // als gar nicht.
    }
    setZeigen(true);
  }, []);

  if (!zeigen) return null;
  return (
    <div className="home-hinweis" role="status" data-home-hinweis>
      <Icon name="export" size={18} />
      <p>
        Über <strong>Teilen › Zum Home-Bildschirm</strong>: mehr Bild, keine Adressleiste.
      </p>
      <button
        className="ghost small"
        aria-label="Hinweis schließen"
        onClick={() => {
          try {
            localStorage.setItem(GESEHEN, 'ja');
          } catch {
            // Dann eben nur für diesmal.
          }
          setZeigen(false);
        }}
      >
        ✕
      </button>
    </div>
  );
}
